import { FC, useRef } from "react";
import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Info as InfoIcon } from "@material-ui/icons";

import IrrMoneyInput from "./IrrMoneyInput";

const useStyles = makeStyles(theme => ({
  tableHeadCell: {
    backgroundColor: theme.custom.palette.gray.light,
  },
  yearCell: {
    minWidth: "120px",
  },
  cashflowCell: {
    width: "200px",
    minWidth: "200px",
    padding: theme.spacing(0.75, 2),
    [theme.breakpoints.down("xs")]: {
      width: "auto",
      minWidth: "auto",
      padding: theme.spacing(0.75, 1),
    },
  },
  infoIconSmall: {
    fontSize: "0.875rem",
    marginLeft: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    cursor: "help",
  },
  exitPriceLabel: {
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
    },
  },
  clickableRow: {
    cursor: "pointer",
  },
}));

interface CashflowTableProps {
  cashflows: string[];
  startYear: number;
  showHeader: boolean;
  onCashflowChange: (index: number, value: string) => void;
  onBlur?: () => void;
  showExitPrice?: boolean;
  exitPrice?: string;
  onExitPriceChange?: (value: string) => void;
}

const CashflowTable: FC<CashflowTableProps> = ({
  cashflows,
  startYear,
  showHeader,
  onCashflowChange,
  onBlur,
  showExitPrice = false,
  exitPrice = "0.00",
  onExitPriceChange,
}) => {
  const classes = useStyles();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const exitPriceInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Table size="small" stickyHeader>
      {showHeader && (
        <TableHead>
          <TableRow>
            <TableCell
              className={`${classes.yearCell} ${classes.tableHeadCell}`}
            >
              <strong>Year</strong>
            </TableCell>
            <TableCell
              align="right"
              className={`${classes.cashflowCell} ${classes.tableHeadCell}`}
            >
              <strong>Cashflow</strong>
            </TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {cashflows.map((cashflow, index) => {
          const year = startYear + index;
          const cashflowIndex = year - 1;

          return (
            <TableRow
              hover
              key={year}
              onClick={() => inputRefs.current[index]?.focus()}
              className={classes.clickableRow}
            >
              <TableCell className={classes.yearCell}>
                <Typography variant="body2">{year}</Typography>
              </TableCell>
              <TableCell align="right" className={classes.cashflowCell}>
                <IrrMoneyInput
                  value={cashflow}
                  onChange={value => onCashflowChange(cashflowIndex, value)}
                  onBlur={onBlur}
                  inputRef={el => {
                    inputRefs.current[index] = el;
                  }}
                />
              </TableCell>
            </TableRow>
          );
        })}
        {showExitPrice && (
          <TableRow
            hover
            onClick={() => exitPriceInputRef.current?.focus()}
            className={classes.clickableRow}
          >
            <TableCell className={classes.yearCell}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" className={classes.exitPriceLabel}>
                  <strong>Exit price</strong>
                </Typography>
                <Tooltip
                  title="This assumes an exit after the 10th year."
                  placement="bottom"
                >
                  <InfoIcon className={classes.infoIconSmall} />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell align="right" className={classes.cashflowCell}>
              <IrrMoneyInput
                value={exitPrice}
                onChange={value => onExitPriceChange?.(value)}
                onBlur={onBlur}
                inputRef={exitPriceInputRef}
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default CashflowTable;
